import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Bell, LogOut, Menu, Search, ChevronDown, RefreshCw, 
  Clock, CheckCircle, AlertTriangle, MessageCircle, 
  Pause, MessageSquare, BadgeAlert, CalendarClock 
} from 'lucide-react';
import { fetchSubmissions, updateStatus, updateVideoLink, statusOptions, updateEligible, updateNotes } from '../../services/submissions.service';
import { sendVideoSMS } from '../../services/notifications.service';
import { sendVideoEmail } from '../../services/notifications.service';
import StatusBadge from './StatusBadge';
import SubmissionModal from './SubmissionModal';
import SMSNotificationSystem from './SMSNotificationSystem';
import SMSConversation from './SMSConversation';
import { generateClient } from '@aws-amplify/api';
import * as subscriptions from '../../graphql/subscriptions';
import * as mutations from '../../graphql/mutations';  // Ensure this contains markMessagesAsRead
import { getSubmission } from '../../graphql/queries';
import { markMessagesAsRead } from '../../graphql/mutations';
import { Link } from 'react-router-dom';

const client = generateClient();

const Dashboard = () => {
  // Authentication state
  const { currentUser, logout } = useAuth();
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentTab, setCurrentTab] = useState('all');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('latest');
  
  // Data state
  const [submissions, setSubmissions] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillReport, setBackfillReport] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Load submissions on component mount
  useEffect(() => {
    loadSubmissions();
  }, []);

  // Subscribe to new message notifications
  useEffect(() => {
    const subscription = client.graphql({
      query: subscriptions.onNewUnreadMessage
    }).subscribe({
      next: ({ data }) => {
        const notification = data.onNewUnreadMessage;
        handleNewMessageNotification(notification);
      },
      error: error => console.error('Subscription error:', error)
    });
    
    return () => subscription.unsubscribe();
  }, []);



  
  // Handle new message notification
  const handleNewMessageNotification = (notification) => {
    // Update unread counts
    setUnreadCounts(prev => {
      const newCounts = {
        ...prev,
        [notification.submissionId]: notification.unreadCount
      };
      // Calculate total unread count with new counts
      calculateTotalUnreadCount(newCounts);
      return newCounts;
    });
    
    // Update the submission with new message indicator
    setSubmissions(prev => prev.map(sub => {
      if (sub.id === notification.submissionId) {
        return {
          ...sub,
          hasUnreadMessages: true,
          lastMessagePreview: notification.lastMessagePreview,
          lastMessageTime: notification.timestamp
        };
      }
      return sub;
    }));
    
    // Show notification if the submission is not currently open
    if (!selectedSubmission || selectedSubmission.id !== notification.submissionId) {
      showNotification(`New message from ${notification.mobileNumber}`, 'info');
    }
  };

  // Calculate total unread count
  const calculateTotalUnreadCount = (counts = unreadCounts) => {
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    setTotalUnreadCount(total);
  };

  // Load submissions from API
  const loadSubmissions = async () => {
    try {
      setIsLoadingData(true);
      setDataError(null);
      const data = await fetchSubmissions();
      console.log("Fetched submissions:", data);
      
      // Initialize unread counts
      const counts = {};
      data.forEach(submission => {
        if (submission.unreadMessageCount) {
          counts[submission.id] = submission.unreadMessageCount;
        }
      });
      setUnreadCounts(counts);
      calculateTotalUnreadCount(counts);
      
      setSubmissions(data);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setDataError('Failed to load submissions. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Filter submissions based on current tab and search
  const filteredSubmissions = submissions.filter(submission => {
    // First filter by tab
    const tabMatches = currentTab === 'all' || submission.status === currentTab;
    
    // Then filter by search term if present
    if (!searchTerm) return tabMatches;
    
    const searchLower = searchTerm.toLowerCase();
    return tabMatches && (
      submission.mobilenumber?.toLowerCase().includes(searchLower) ||
      submission.zipcode?.toLowerCase().includes(searchLower) ||
      submission.age?.toLowerCase().includes(searchLower)
    );
  });

  // Sort submissions
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    if (sortOrder === 'latest') {
      return new Date(b.submittedDate) - new Date(a.submittedDate);
    } else if (sortOrder === 'unreadMessages') {
      const aUnread = unreadCounts[a.id] || 0;
      const bUnread = unreadCounts[b.id] || 0;
      return bUnread - aUnread;
    } else if (sortOrder === 'lastMessage') {
      const aTime = a.lastMessageTime ? new Date(a.lastMessageTime) : new Date(0);
      const bTime = b.lastMessageTime ? new Date(b.lastMessageTime) : new Date(0);
      return bTime - aTime;
    }
    return 0;
  });

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Backfill: preview impacted records, then confirm to execute
  const handleBackfillPreview = async () => {
    setBackfillRunning(true);
    setBackfillReport(null);
    try {
      const { previewBackfillAppointmentStatuses } = await import('../../services/appointments.service');
      const preview = await previewBackfillAppointmentStatuses();
      setBackfillReport({ ...preview, confirmed: false });
      if (preview.impacted.length === 0) {
        showNotification('No appointments need updating');
      }
    } catch (err) {
      console.error('Backfill preview failed:', err);
      showNotification('Backfill preview failed', 'error');
    } finally {
      setBackfillRunning(false);
    }
  };

  const handleBackfillConfirm = async () => {
    if (!backfillReport?.impacted?.length) return;
    setBackfillRunning(true);
    try {
      const { executeBackfillAppointmentStatuses } = await import('../../services/appointments.service');
      const result = await executeBackfillAppointmentStatuses(backfillReport.impacted);
      setBackfillReport({ ...backfillReport, ...result, confirmed: true });
      showNotification(`Backfill complete: ${result.updated.length} updated, ${result.errors.length} errors`);
    } catch (err) {
      console.error('Backfill execution failed:', err);
      showNotification('Backfill execution failed', 'error');
    } finally {
      setBackfillRunning(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (id, newStatus) => {
    try {
      console.log("Updating status with:", { id, newStatus });
      await updateStatus(id, newStatus);
      
      // Update local state for immediate UI feedback
      setSubmissions(submissions.map(sub => 
        sub.id === id ? {...sub, status: newStatus} : sub
      ));
      
      // Update selected submission if open
      if (selectedSubmission && selectedSubmission.id === id) {
        setSelectedSubmission({...selectedSubmission, status: newStatus});
      }

      // When submission marked as "Assessment Completed", mark latest scheduled appointment as COMPLETED
      if (newStatus === 'Assessment Completed') {
        try {
          const { fetchAppointmentsBySubmission, updateAppointmentRecord } = await import('../../services/appointments.service');
          const appointments = await fetchAppointmentsBySubmission(id);
          const scheduledAppt = appointments.find(a => a.status === 'SCHEDULED');
          if (scheduledAppt) {
            await updateAppointmentRecord({ id: scheduledAppt.id, status: 'COMPLETED' });
            console.log('✅ Appointment marked as COMPLETED');
          }
        } catch (apptErr) {
          console.error('⚠️ Failed to update appointment status:', apptErr);
        }
      }
      
      showNotification(`Status updated to "${newStatus}"`);
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('Failed to update status', 'error');
    }
  };

  // Handle eligibility update
  const handleUpdateEligibility = async (id, eligibleValue) => {
    try {
      console.log("Updating eligibility with:", { id, eligibleValue });
      await updateEligible(id, eligibleValue);
      
      // Update local state for immediate UI feedback
      setSubmissions(submissions.map(sub => 
        sub.id === id ? {...sub, eligible: eligibleValue} : sub
      ));
      
      // Update selected submission if open
      if (selectedSubmission && selectedSubmission.id === id) {
        setSelectedSubmission({...selectedSubmission, eligible: eligibleValue});
      }
      
      showNotification(`Eligible updated to "${eligibleValue}"`);
    } catch (error) {
      console.error('Error updating eligible:', error);
      showNotification('Failed to update eligible', 'error');
    }
  };

  // Handle eligibility update
  const handleUpdateNotes = async (id, notes) => {
    try {
      console.log("Updating Notes with:", { id, notes });
  
      // Save notes to DynamoDB
      await updateNotes(id, notes);
  
  
      // Update the local state with the new submission data
      setSubmissions(prevSubmissions =>
        prevSubmissions.map(sub =>
          sub.id === id ? { ...sub, Notes: notes } : sub
        )
      );
  
      if (selectedSubmission?.id === id) {
        setSelectedSubmission(prev => ({
          ...prev,
          Notes: notes
        }));
      }
  
      showNotification(`Notes updated successfully.`);
    } catch (error) {
      console.error('Error updating notes:', error);
      showNotification('Failed to update notes', 'error');
    }
  };
  

  // Handle sending video link
  const handleSendVideoLink = async (id, link) => {
    try {
      // Update video link
      await updateVideoLink(id, link);
      
      // Update local state for immediate UI feedback
      setSubmissions(submissions.map(sub => 
        sub.id === id ? {...sub, videoLink: link} : sub
      ));
      
      // Update selected submission if open
      if (selectedSubmission && selectedSubmission.id === id) {
        setSelectedSubmission({...selectedSubmission, videoLink: link});
      }
      
      showNotification(link ? 'Video link generated successfully' : 'Video link removed');
      return true;
    } catch (error) {
      console.error('Error updating video link:', error);
      showNotification('Failed to update video link', 'error');
      return false;
    }
  };
  
  // Handle sending SMS with video link
  const handleSendVideoSMS = async (fromNumber, toNumber, message) => {
    try {
      await sendVideoSMS(fromNumber, toNumber, message);
      showNotification('SMS sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending SMS:', error);
      showNotification('Failed to send SMS', 'error');
      throw error;
    }
  };

  // Handle sending email with video link
  const handleSendVideoEmail = async (toEmail, subject, message, videoLink) => {
    try {
      await sendVideoEmail(toEmail, subject, message, videoLink);
      showNotification('Email sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending email:', error);
      showNotification('Failed to send email', 'error');
      throw error;
    }
  };

  // Open submission details
  const openSubmissionDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
  };

  // Open SMS conversation
  // Handle data refresh from SID API
  const handleDataRefresh = (updatedSubmission) => {
    // Update the selected submission
    setSelectedSubmission(updatedSubmission);
    
    // Update the submission in the main list
    setSubmissions(prevSubmissions => 
      prevSubmissions.map(sub => 
        sub.id === updatedSubmission.id ? updatedSubmission : sub
      )
    );
  };

  const openSMSConversation = async (submission) => {
    try {
      setSelectedSubmission(submission);
      
      // Get submission with conversations
      const submissionData = await client.graphql({
        query: getSubmission,
        variables: { id: submission.id }
      });
      
      // Check if there's an existing conversation
      const conversations = submissionData.data.getSubmission.conversations.items;
      
      if (!conversations || conversations.length === 0) {
        console.error("No conversations found for this submission");
        showNotification('No conversation found for this submission', 'error');
        return;
      }
      
      // Use the first conversation (or you could sort by lastMessageTime to get the most recent)
      const conversationId = conversations[0].id;
      console.log("Using conversation:", conversationId);
      
      setSelectedConversation({
        id: conversationId,
        submissionId: submission.id
      });
      
      // Mark messages as read if needed
      if (conversations[0].unreadCount > 0) {
        const result = await client.graphql({
          query: markMessagesAsRead,
          variables: { conversationId }
        });
        
        // Update UI state for unread counts
        if (result.data.markMessagesAsRead.success) {
          setUnreadCounts(prev => {
            const newCounts = { ...prev };
            delete newCounts[submission.id]; // Remove unread count for this submission
            calculateTotalUnreadCount(newCounts);
            return newCounts;
          });
          
          // Update the submission to remove unread indicator
          setSubmissions(prev => prev.map(sub => {
            if (sub.id === submission.id) {
              return {
                ...sub,
                hasUnreadMessages: false,
                unreadMessageCount: 0
              };
            }
            return sub;
          }));
        }
      }
      
      setShowSMSModal(true);
    } catch (error) {
      console.error('Error opening conversation:', error);
      showNotification('Failed to open conversation', 'error');
    }
  };

  // Create a new conversation if needed
  const createConversation = async (submissionId) => {
    // This would be an API call to create a conversation
    // For now, we'll just mock it
    return `conversation-${submissionId}`;
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format time for last message
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };
  
  const hideUserScoreForEmail = "mtm2226@cumc.columbia.edu"; // Replace with the actual email
  const shouldHideUserScore = currentUser?.email === hideUserScoreForEmail;
  
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Notification system for real-time updates */}
      <SMSNotificationSystem onUnreadUpdate={handleNewMessageNotification} />
      
      {/* Sidebar */}
      <div className={`bg-gray-900 text-white transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 flex items-center justify-between">
          {sidebarOpen ? (
            <h1 className="text-xl font-bold">Submission Hub</h1>
          ) : (
            <h1 className="text-xl font-bold">SH</h1>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white">
            <Menu size={20} />
          </button>
        </div>
        
        <nav className="mt-8">
          <ul>
            <li 
              className={`px-4 py-3 flex items-center ${currentTab === 'all' ? 'bg-blue-600' : 'hover:bg-gray-800'} cursor-pointer`}
              onClick={() => setCurrentTab('all')}
            >
              <Menu size={20} />
              {sidebarOpen && <span className="ml-3">All Submissions</span>}
            </li>
            
            <li 
              className={`px-4 py-3 flex items-center ${currentTab === 'unread_messages' ? 'bg-blue-600' : 'hover:bg-gray-800'} cursor-pointer`}
              onClick={() => setCurrentTab('unread_messages')}
            >
              <MessageCircle size={20} />
              {sidebarOpen && (
                <div className="ml-3 flex items-center justify-between w-full">
                  <span>Unread Messages</span>
                  {totalUnreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                      {totalUnreadCount}
                    </span>
                  )}
                </div>
              )}
              {!sidebarOpen && totalUnreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-2">
                  {totalUnreadCount}
                </span>
              )}
            </li>

            
            <li 
              className={`px-4 py-3 flex items-center justify-between ${statusOptions.some(s => s.id === currentTab) ? 'bg-blue-600' : 'hover:bg-gray-800'}`}
            >
              <div className="flex items-center">
                <BadgeAlert size={20} />
                {sidebarOpen && (
                  <span className="ml-3">
                    {statusOptions.find(s => s.id === currentTab)?.label || 'Filter by Status'}
                  </span>
                )}
              </div>
              {sidebarOpen && (
                <ChevronDown 
                  size={16} 
                  className={`transition-transform cursor-pointer hover:text-gray-300 ${statusDropdownOpen ? 'rotate-180' : ''}`}
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                />
              )}
            </li>
            {statusDropdownOpen && sidebarOpen && (
              <ul className="bg-gray-800">
                {statusOptions.filter(status => status.id !== 'Archive').map(status => (
                  <li 
                    key={status.id}
                    className={`px-6 py-2 flex items-center ${currentTab === status.id ? 'bg-blue-600' : 'hover:bg-gray-700'} cursor-pointer text-sm`}
                    onClick={() => setCurrentTab(status.id)}
                  >
                    <span className={`w-2 h-2 rounded-full mr-2 ${status.bgColor.replace('bg-', 'bg-').replace('-100', '-400')}`}></span>
                    {status.label}
                  </li>
                ))}
              </ul>
            )}
          </ul>          
          <ul>
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/manage-slots" className="flex items-center w-full text-white">
                <Clock size={20} />
                {sidebarOpen && <span className="ml-3">Manage Appoinments</span>}
              </Link>
            </li>
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <Link to="/message-templates" className="flex items-center w-full text-white">
                <MessageSquare size={20} />
                {sidebarOpen && <span className="ml-3">Message Templates</span>}
              </Link>
            </li>
            <li className="px-4 py-3 flex items-center hover:bg-gray-800 cursor-pointer">
              <a href={process.env.REACT_APP_MRT_TESTING_URL} target="_blank" rel="noopener noreferrer" className="flex items-center w-full text-white">
                <BadgeAlert size={20} />
                {sidebarOpen && <span className="ml-3">MRT Testing</span>}
              </a>
            </li>
          </ul>
        </nav>
        
        <div className="absolute bottom-0 left-0 p-4 space-y-2">
          <div
            className={`flex items-center cursor-pointer hover:bg-gray-800 p-2 rounded ${backfillRunning ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={handleBackfillPreview}
          >
            <RefreshCw size={20} className={backfillRunning ? 'animate-spin' : ''} />
            {sidebarOpen && <span className="ml-3 text-sm">{backfillRunning ? 'Running...' : 'Backfill Appts'}</span>}
          </div>
          <div className="flex items-center cursor-pointer hover:bg-gray-800 p-2 rounded" onClick={handleLogout}>
            <LogOut size={20} />
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation */}
        <header className="bg-white shadow-sm z-10">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center rounded-md bg-gray-100 p-2">
              <Search size={18} className="text-gray-500" />
              <input 
                type="text" 
                placeholder="Search submissions..." 
                className="bg-transparent border-none ml-2 focus:outline-none w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex items-center">
              <button className="p-2 mr-2 text-gray-500 hover:text-gray-700 relative">
                <Bell size={20} />
                {totalUnreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center">
                    {totalUnreadCount}
                  </span>
                )}
              </button>
              
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
                  {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                {sidebarOpen && (
                  <div className="ml-2">
                    <p className="text-sm font-medium text-gray-700">{currentUser?.email || 'User'}</p>
                    <p className="text-xs text-gray-500">Admin</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        {/* Main dashboard content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {/* Notification */}
          {notification && (
            <div className={`mb-4 p-3 rounded-lg ${
              notification.type === 'error' ? 'bg-red-100 text-red-800' : 
              notification.type === 'info' ? 'bg-blue-100 text-blue-800' : 
              'bg-green-100 text-green-800'
            }`}>
              {notification.message}
            </div>
          )}
          
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">
              {currentTab === 'all' ? 'All Submissions' : 
              currentTab === 'unread_messages' ? 'Unread Messages' :
              statusOptions.find(s => s.id === currentTab)?.label || 'Submissions'}
            </h1>
            <div className="flex items-center space-x-4">
              <button 
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                onClick={loadSubmissions}
                disabled={isLoadingData}
              >
                <RefreshCw size={16} className={isLoadingData ? "animate-spin" : ""} />
                <span>Refresh Data</span>
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                Export Data
              </button>
              <div className="relative">
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="appearance-none bg-white border rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="latest">Latest first</option>
                  <option value="unreadMessages">Unread messages</option>
                  <option value="lastMessage">Recent messages</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDown size={16} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Loading and error states */}
          {isLoadingData && (
            <div className="bg-blue-50 text-blue-700 p-4 rounded-lg mb-4 flex items-center justify-center">
              <RefreshCw size={20} className="animate-spin mr-2" />
              <span>Loading submission data...</span>
            </div>
          )}
          
          {dataError && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
              <p className="font-medium">Error loading data</p>
              <p>{dataError}</p>
              <button 
                onClick={loadSubmissions}
                className="mt-2 text-sm bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded"
              >
                Try Again
              </button>
            </div>
          )}
          
          {/* Submissions table - Cross-browser Mac compatible version */}
          <div className="bg-white rounded-lg shadow">
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div style={{ minWidth: "1000px" }}>
                <table className="w-full border-collapse">
                  <colgroup>
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mobile Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Participants ID
                      </th>                  
                      {!shouldHideUserScore && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Score
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Age
                      </th>                  
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submission Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Messages
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSubmissions.length === 0 && !isLoadingData ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500 border-b border-gray-200">
                          <div className="flex flex-col items-center justify-center">
                            <p className="mb-2 text-lg">No submissions found</p>
                            <p className="text-sm">Try changing filters or refreshing the data</p>
                            <button 
                              onClick={loadSubmissions} 
                              className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Refresh Data
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sortedSubmissions.map((submission) => (
                        <tr 
                          key={submission.id} 
                          className={`border-b border-gray-200 hover:bg-gray-50 ${submission.hasUnreadMessages ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">{submission.mobilenumber}</div>
                            {submission.needsAlternateTime && (
                              <span
                                title="Participant requested alternate appointment times"
                                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                              >
                                <CalendarClock size={12} />
                                Needs Time
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900 overflow-hidden text-ellipsis" style={{ maxWidth: "100%" }}>
                              {submission.publicId}
                            </div>
                          </td>                      
                          {!shouldHideUserScore && (
                            <td className="px-4 py-4">
                              <div className="text-sm text-gray-900">{submission.userscore}</div>
                            </td>
                          )}
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">{submission.age}</div>
                          </td>                      
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-500">{formatDate(submission.submittedDate)}</div>
                          </td>
                          <td className="px-4 py-4">
                            <StatusBadge status={submission.status} />
                          </td>
                          <td className="px-4 py-4">
                            <button
                              onClick={() => openSMSConversation(submission)}
                              className="flex items-center text-sm text-gray-600 hover:text-blue-600 group"
                            >
                              <div className="relative">
                                <MessageSquare size={18} className="text-gray-400 group-hover:text-blue-500" />
                                {unreadCounts[submission.id] > 0 && (
                                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                    {unreadCounts[submission.id]}
                                  </span>
                                )}
                              </div>
                              <span className="ml-2 hidden sm:inline-block" style={{ maxWidth: "120px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                {submission.lastMessagePreview ? (
                                  <>
                                    <span>
                                      {submission.lastMessagePreview.length > 25 
                                        ? `${submission.lastMessagePreview.substring(0, 25)}...` 
                                        : submission.lastMessagePreview}
                                    </span>
                                    <span className="text-xs text-gray-400 ml-1">
                                      {formatMessageTime(submission.lastMessageTime)}
                                    </span>
                                  </>
                                ) : (
                                  "Start conversation"
                                )}
                              </span>
                            </button>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button 
                              onClick={() => openSubmissionDetails(submission)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
      
      {/* Submission Details Modal */}
      {showModal && selectedSubmission && (
        <SubmissionModal
          submission={selectedSubmission}
          onClose={() => setShowModal(false)}
          onStatusChange={handleStatusChange}
          onUpdateEligibility={handleUpdateEligibility}
          onUpdateNotes={handleUpdateNotes}
          onVideoLinkUpdate={handleSendVideoLink}
          onSendVideoSMS={handleSendVideoSMS}
          onSendVideoEmail={handleSendVideoEmail}
          formatDate={formatDate}
          onOpenSMSConversation={() => {
            setShowModal(false);
            openSMSConversation(selectedSubmission);
          }}
          onDataRefresh={handleDataRefresh}
        />
      )}
      
      {/* SMS Conversation Modal */}
      {showSMSModal && selectedSubmission && selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-4/5 flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3">
                  <MessageCircle size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">SMS Conversation</h2>
                  <p className="text-sm text-gray-500">{selectedSubmission.mobilenumber}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowSMSModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden">
              <SMSConversation 
                submissionId={selectedSubmission.id}
                conversationId={selectedConversation.id}
              />
            </div>
          </div>
        </div>
      )}
      {/* Backfill Preview/Report Modal */}
      {backfillReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                {backfillReport.confirmed ? 'Backfill Complete' : 'Backfill Preview'}
              </h2>
              <button onClick={() => setBackfillReport(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="space-y-4 text-sm">
              <p><strong>Submissions checked:</strong> {backfillReport.checked}</p>
              <p><strong>{backfillReport.confirmed ? 'Updated' : 'Will be updated'}:</strong> {backfillReport.confirmed ? backfillReport.updated?.length || 0 : backfillReport.impacted?.length || 0}</p>
              <p><strong>Skipped:</strong> {backfillReport.skipped?.length || 0}</p>
              <p><strong>Errors:</strong> {backfillReport.errors?.length || 0}</p>

              {/* Preview table (before confirm) */}
              {!backfillReport.confirmed && backfillReport.impacted?.length > 0 && (
                <div>
                  <h3 className="font-semibold mt-3 mb-1">Impacted Appointments</h3>
                  <table className="w-full text-xs border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">Mobile</th>
                        <th className="p-2 text-left">Slot Date</th>
                        <th className="p-2 text-left">Time</th>
                        <th className="p-2 text-left">Current Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backfillReport.impacted.map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{item.mobile}</td>
                          <td className="p-2">{item.slotDate}</td>
                          <td className="p-2">{item.slotTime}</td>
                          <td className="p-2">{item.currentStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={handleBackfillConfirm}
                      disabled={backfillRunning}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                    >
                      {backfillRunning ? 'Updating...' : `Confirm & Update ${backfillReport.impacted.length} Appointments`}
                    </button>
                    <button
                      onClick={() => setBackfillReport(null)}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Results table (after confirm) */}
              {backfillReport.confirmed && backfillReport.updated?.length > 0 && (
                <div>
                  <h3 className="font-semibold mt-3 mb-1 text-green-600">Successfully Updated</h3>
                  <table className="w-full text-xs border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 text-left">Mobile</th>
                        <th className="p-2 text-left">Slot Date</th>
                        <th className="p-2 text-left">Time</th>
                        <th className="p-2 text-left">Previous Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backfillReport.updated.map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{item.mobile}</td>
                          <td className="p-2">{item.slotDate}</td>
                          <td className="p-2">{item.slotTime}</td>
                          <td className="p-2">{item.currentStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {backfillReport.errors?.length > 0 && (
                <div>
                  <h3 className="font-semibold mt-3 mb-1 text-red-600">Errors</h3>
                  <ul className="list-disc pl-5">
                    {backfillReport.errors.map((err, i) => (
                      <li key={i}>{err.submissionId}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;